import { useEffect, useState } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Card, CardContent } from "@component/card";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { CalendarIcon, PlusIcon, TrashIcon, PenSquareIcon, Loader2Icon } from "lucide-react";
import { Badge } from "@component/badge";
import { TwoPanelContentLayout } from "./two-panel-content-layout";
import { cn } from "@util/tailwind";
import { Applicant } from "@type/applicant";
import { addMultipleApplicantScheduleNote, getApplicantReviewScheduled, updateApplicantScheduleNote } from "@service/job";
import { catchErrorLog } from "@util/sentry";
import { useSnackbar } from "@hook/use-snackbar";
import { Job, ReviewScheduledNoteItem } from "@type/job";
import { useUserProfileContext } from "@context/user_profile";
import { formatDate, formatHumanReadable } from "@util/date";
import { convertCapitalizeWords } from "@util/string";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    job: Job | null;
    applicants: Applicant[]
    onReload: () => void;
}

export default function ReviewScheduleManagerDialog({
    isOpen,
    onClose,
    job,
    applicants = [],
    onReload,
}: Props) {
    const snackbar = useSnackbar();
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    // Sort reviews by most recent first (reverse chronological order)
    const [reviews, setReviews] = useState<ReviewScheduledNoteItem[]>([]);

    // Initialize state based on whether there are existing reviews
    const hasExistingReviews = false;
    const [selectedReviewIndex, setSelectedReviewIndex] = useState<number | null>(null);
    const [isAddingNew, setIsAddingNew] = useState(!hasExistingReviews);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [reviewToDeleteIndex, setReviewToDeleteIndex] = useState<number | null>(null);

    // Form state for new/editing review
    const [formData, setFormData] = useState<ReviewScheduledNoteItem>({
        review_type: "interview",
        review_name: "",
        interview_type: "recruiter",
        notes: "",
        interview_date: "",
        action_at: "",
        action_by: "",
    });

    useEffect(() => {
        if (isOpen && job && applicants.length === 1) {
            getApplicantReviewScheduled(job.id, applicants[0].applicant_id).then((resp) => {
                if (resp.status === 200) {
                    const fetchedReviews = resp.data.data.review_schedule_notes || [];
                    // Sort reviews by most recent first
                    fetchedReviews.sort((a: ReviewScheduledNoteItem, b: ReviewScheduledNoteItem) => {
                        const dateA = new Date(a.action_at || "").getTime();
                        const dateB = new Date(b.action_at || "").getTime();
                        return dateB - dateA;
                    });
                    setReviews(fetchedReviews);
                }
            }).catch((error) => {
                catchErrorLog(`failed to fetch applicant review scheduled notes: ${error}`);
                snackbar.showError("Failed to load scheduled reviews. Please try again.");
            });
        }
    }, [isOpen, job?.id, applicants[0]?.applicant_id]);

    const handleSelectReview = (reviewIndex: number) => {
        setSelectedReviewIndex(reviewIndex);
        setIsAddingNew(false);
        setIsEditing(false);
    };

    const handleStartEdit = () => {
        const review = reviews[selectedReviewIndex || 0];
        if (!review) return;

        setFormData({
            review_type: review.review_type,
            review_name: review.review_name,
            interview_type: review.interview_type,
            notes: review.notes,
            interview_date: review.interview_date || "",
        });
        setIsEditing(true);
    };

    const handleStartAdd = () => {
        setIsAddingNew(true);
        setSelectedReviewIndex(null);
        setIsEditing(false);
        setFormData({
            review_type: "interview",
            review_name: "",
            interview_type: "recruiter",
            notes: "",
            interview_date: "",
        });
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setIsAddingNew(false);
        setFormData({
            review_type: "interview",
            review_name: "",
            interview_type: "recruiter",
            notes: "",
            interview_date: "",
        });
    };

    const isFormValid = () => {
        if (!formData.review_name.trim()) return false;
        if (formData.review_type === "interview") {
            if (!formData.interview_type) return false;
            if (!formData.interview_date) return false;
        }
        return true;
    };

    const handleSaveEdit = () => {
        if (!isFormValid()) {
            return;
        }

        let updatedReviews: ReviewScheduledNoteItem[];

        if (isEditing && selectedReviewIndex !== null) {
            // Update existing review
            updatedReviews = reviews.map((review, index) =>
                index === selectedReviewIndex
                    ? {
                        ...review,
                        ...formData,
                        action_at: new Date().toISOString(),
                    }
                    : review
            );
        } else if (isAddingNew) {
            // Add new review
            const newReview: ReviewScheduledNoteItem = {
                ...formData,
                action_by: currentUserInfo?.first_name,
                action_at: new Date().toISOString(),
            };
            updatedReviews = [newReview, ...reviews]; // Add new review at the beginning
            setSelectedReviewIndex(0); // Select the newly created review
        } else {
            return;
        }

        onUpdateApplicantStatusReviewScheduled(
            applicants.map(applicant => applicant.applicant_id),
            updatedReviews
        );
    };

    const handleDeleteClick = (reviewIndex: number) => {
        setReviewToDeleteIndex(reviewIndex);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (reviewToDeleteIndex === null) return;

        const updatedReviews = reviews.filter(
            (review, index) => index !== reviewToDeleteIndex
        );

        updateApplicantScheduleNote(
            job?.id || "",
            applicants[0].applicant_id,
            {
                review_schedule_notes: updatedReviews,
            }
        ).then((resp) => {
            if (resp.status === 200) {
                setReviews(updatedReviews);
                setSelectedReviewIndex(null);
                setShowDeleteConfirm(false);
                setReviewToDeleteIndex(null);
            }
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status. Please try again.");
        }).finally(() => {
            onReload();
        })
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setReviewToDeleteIndex(null);
    };

    const selectedReview = reviews.find((r, index) => index === selectedReviewIndex);

    const onUpdateApplicantStatusReviewScheduled = (applicant_ids: string[], review_schedule_notes: ReviewScheduledNoteItem[]) => {
        setIsSubmitting(true);
        if (applicant_ids.length === 1) {
            updateApplicantScheduleNote(
                job?.id || "",
                applicant_ids[0],
                {
                    review_schedule_notes: review_schedule_notes,
                }
            ).then((resp) => {
                if (resp.status === 200) {
                    snackbar.showSuccess("Scheduled review note saved successfully.");
                    setReviews(review_schedule_notes);
                    // Reset form and exit edit/add mode
                    setIsEditing(false);
                    setIsAddingNew(false);
                    setFormData({
                        review_type: "interview",
                        review_name: "",
                        interview_type: "recruiter",
                        notes: "",
                        interview_date: "",
                    });
                }
            }).catch((error) => {
                catchErrorLog(`failed to update applicant status badge: ${error}`);
                onClose();
                snackbar.showError("Failed to update applicant status. Please try again.");
            }).finally(() => {
                setIsSubmitting(false);
                onReload();
            })
        } else if (applicant_ids.length > 1) {
            addMultipleApplicantScheduleNote(job?.id || "", {
                user_ids: applicant_ids,
                review_schedule_note: review_schedule_notes[0],
            }).then((resp) => {
                if (resp.status === 200) {
                    snackbar.showSuccess("Scheduled review note saved successfully.");
                    setReviews(review_schedule_notes);

                    // Reset form and exit edit/add mode
                    setIsEditing(false);
                    setIsAddingNew(false);
                    setFormData({
                        review_type: "interview",
                        review_name: "",
                        interview_type: "recruiter",
                        notes: "",
                        interview_date: "",
                    });
                }
            }).catch((error) => {
                catchErrorLog(`failed to update applicant status badge: ${error}`);
                onClose();
                snackbar.showError("Failed to update applicant status. Please try again.");
            }).finally(() => {
                setIsSubmitting(false);
                onReload();
            })
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={cn("max-h-[85vh] overflow-hidden p-8", {
                "max-w-6xl": applicants.length === 1,
            })}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <CalendarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        {applicants.length === 1 ? (
                            <>
                                Scheduled Reviews - {applicants[0].applicant_name}
                            </>
                        ) : (
                            <>
                                Scheduled Reviews
                            </>
                        )}

                    </DialogTitle>
                    {applicants.length === 1 ? (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            Manage your scheduled reviews for {applicants[0].applicant_name}. These details
                            will be shared internally with your team and won't be sent to the
                            candidate.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            Add review details for {applicants.length} candidates. This is for internal tracking only and won't be shared with the candidates.
                        </p>
                    )}

                </DialogHeader>

                <div className={cn("max-h-[550px]", {
                    "h-[calc(85vh-160px)]": applicants.length === 1
                })}>
                    <TwoPanelContentLayout
                        className={cn("h-full", {
                            "grid-cols-1 lg:grid-cols-[40%_60%]": applicants.length === 1,
                        })}
                        leftPanel={applicants.length === 1 ? (
                            <>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            Reviews ({reviews.length})
                                        </h3>
                                        <Button
                                            onClick={handleStartAdd}
                                            size="sm"
                                            className="bg-blue-600 hover:bg-blue-700 text-white h-8 w-8 p-0"
                                            disabled={isAddingNew || isEditing}
                                        >
                                            <PlusIcon className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div
                                    className="space-y-2 pb-6"
                                    style={{
                                        paddingTop: "2px",
                                        paddingRight: "2px",
                                        paddingBottom: "2px",
                                        paddingLeft: "2px",
                                    }}
                                >
                                    {reviews.length === 0 && (
                                        <div className="text-center py-12">
                                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                                No reviews scheduled yet.
                                            </p>
                                        </div>
                                    )}

                                    {reviews.map((review, index) => (
                                        <Card
                                            key={index}
                                            className={cn(
                                                "cursor-pointer transition-all duration-200 relative group shadow-none",
                                                selectedReviewIndex === index
                                                    ? "ring-2 ring-blue-500 border-blue-500 dark:border-blue-500"
                                                    : "hover:bg-gray-50 hover:border-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-600"
                                            )}
                                        >
                                            <CardContent className="p-4 pr-12">
                                                <div
                                                    className="space-y-2"
                                                    onClick={() => handleSelectReview(index)}
                                                >
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full h-5 px-2.5 text-[11px] font-medium flex-shrink-0 capitalize"
                                                        >
                                                            {convertCapitalizeWords(review.review_type)}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight break-words">
                                                        {review.review_name}
                                                    </p>
                                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                        Scheduled by {review.action_by || "Current User"}
                                                    </p>
                                                </div>
                                            </CardContent>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick(index);
                                                }}
                                                className="absolute top-1/2 -translate-y-1/2 right-3 h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-colors duration-200"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </Button>
                                        </Card>
                                    ))}
                                </div>
                            </>
                        ) : null}
                        rightPanel={
                            <div className={cn({
                                "px-4": applicants.length === 1
                            })}>
                                {isAddingNew || isEditing ? (
                                    // Edit/Add Mode
                                    <div className="space-y-3">
                                        {applicants.length === 1 ? (
                                            <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                                {isAddingNew ? "Add New Review" : "Edit Review"}
                                            </h4>
                                        ) : null}

                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Review Type <span className="text-red-500">*</span>
                                                    </label>
                                                    <Select
                                                        value={formData.review_type}
                                                        onValueChange={(
                                                            value: "interview" | "case_study"
                                                        ) =>
                                                            setFormData({ ...formData, review_type: value })
                                                        }
                                                    >
                                                        <SelectTrigger className="h-10">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="interview">
                                                                Interview
                                                            </SelectItem>
                                                            <SelectItem value="case_study">
                                                                Case Study
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {formData.review_type === "interview" && (
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Interview Type{" "}
                                                            <span className="text-red-500">*</span>
                                                        </label>
                                                        <Select
                                                            value={formData.interview_type}
                                                            onValueChange={(
                                                                value: "recruiter" | "hiring_manager" | "final"
                                                            ) =>
                                                                setFormData({
                                                                    ...formData,
                                                                    interview_type: value,
                                                                })
                                                            }
                                                        >
                                                            <SelectTrigger className="h-10">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="recruiter">
                                                                    Recruiter
                                                                </SelectItem>
                                                                <SelectItem value="hiring_manager">
                                                                    Hiring Manager
                                                                </SelectItem>
                                                                <SelectItem value="final">Final</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Review Name <span className="text-red-500">*</span>
                                                </label>
                                                <Input
                                                    value={formData.review_name}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            review_name: e.target.value,
                                                        })
                                                    }
                                                    placeholder={
                                                        formData.review_type === "interview"
                                                            ? "e.g., Technical Interview with John"
                                                            : "e.g., Product Case Study"
                                                    }
                                                    className="h-10"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {formData.review_type === "interview" ? "Interview Date" : "Deadline"}{" "}
                                                    <span className="text-red-500">*</span>
                                                </label>
                                                <Input
                                                    type="date"
                                                    value={formData.interview_date}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            interview_date: e.target.value,
                                                        })
                                                    }
                                                    min={new Date().toISOString().split("T")[0]}
                                                    className="h-10"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Purpose or Notes
                                                </label>
                                                <Textarea
                                                    value={formData.notes}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            notes: e.target.value,
                                                        })
                                                    }
                                                    placeholder="e.g., evaluate problem solving approach, culture fit, or decision readiness"
                                                    className="min-h-[80px] resize-y"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex gap-2 justify-end pt-3">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleCancelEdit}
                                                className="h-9 px-4"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={handleSaveEdit}
                                                className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4"
                                                disabled={!isFormValid() || isSubmitting}
                                            >
                                                {isSubmitting && <Loader2Icon className="w-4 h-4 mr-1.5 animate-spin" />}
                                                {isAddingNew ? "Schedule Review" : "Save Changes"}
                                            </Button>
                                        </div>
                                    </div>
                                ) : selectedReview ? (
                                    // View Mode
                                    <div className="space-y-6">
                                        <div className="space-y-4">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full h-6 px-3 text-xs font-medium capitalize"
                                                        >
                                                            {convertCapitalizeWords(selectedReview.review_type)}
                                                        </Badge>
                                                    </div>
                                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                                        {selectedReview.review_name}
                                                    </h3>
                                                    {selectedReview.action_by && (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                            Scheduled by {selectedReview.action_by}
                                                        </p>
                                                    )}
                                                    {selectedReview.action_at && (
                                                        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                            <PenSquareIcon className="h-3.5 w-3.5 flex-shrink-0" />

                                                            <span>
                                                                Last Updated: {formatHumanReadable(selectedReview.action_at, true) || "N/A"}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={handleStartEdit}
                                                        className="gap-1.5"
                                                    >
                                                        <PenSquareIcon className="h-4 w-4" />
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteClick(
                                                            reviews.findIndex((r) => r === selectedReview)
                                                        )}
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4 mt-6">
                                            {selectedReview.review_type === "interview" &&
                                                selectedReview.interview_type && (
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Interview Type
                                                        </label>
                                                        <div>
                                                            <Badge
                                                                variant="outline"
                                                                className="rounded-full h-6 px-3 text-xs font-medium border-gray-300 dark:border-gray-600 capitalize"
                                                            >
                                                                {convertCapitalizeWords(selectedReview.interview_type)}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                )}

                                            {selectedReview.interview_date && (
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        {selectedReview.review_type === "interview" ? "Interview Date" : "Deadline"}{" "}
                                                        <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                                                        <p className="text-sm text-gray-700 dark:text-gray-300">
                                                            {formatDate(selectedReview.interview_date)}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Purpose or Notes
                                                </label>
                                                {selectedReview.notes ? (
                                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                                                            {selectedReview.notes}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-dashed border-gray-300 dark:border-gray-700 text-center">
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            No notes added for this review.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Empty state
                                    <div className="flex flex-col items-center justify-center h-full text-center py-20">
                                        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                            <CalendarIcon className="w-8 h-8 text-gray-400 dark:text-gray-600" />
                                        </div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                            No Review Selected.
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Select a scheduled review to view more details.
                                        </p>
                                    </div>
                                )}
                            </div>
                        }
                    />
                </div>
            </DialogContent>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent className="sm:max-w-[425px]">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 dark:bg-red-950">
                            <TrashIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        </div>
                        <DialogTitle className="text-lg font-semibold">
                            Delete Review
                        </DialogTitle>
                    </div>

                    <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        Are you sure you want to delete the review{" "}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                            "{reviews.find((r, rindex) => rindex === reviewToDeleteIndex)?.review_name}"
                        </span>
                        ? This action cannot be undone.
                    </div>

                    <div className="flex gap-2 mt-6 justify-end">
                        <Button variant="outline" onClick={handleCancelDelete}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete Review
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
